import React, { useState, useEffect } from "react";
import { HiXCircle } from "react-icons/hi";
import { Auth } from "../../../Common/lib/auth";
import { ImSpinner8 } from "react-icons/im";

export default function VerificationStep({ userInfo, setUserInfo, currentStep, setCurrentStep, classNames }) {
  const [verifyCode, setVerifyCode] = useState("");
  const [resendCount, setResendCount] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    verifyCodeError: "",
  });

  // Handles clearing errors when a user starts editing a field.
  useEffect(() => {
    setErrors(prevState => ({
      ...prevState,
      verifyCodeError: "",
    }));
  }, [verifyCode]);

  /**
   * Handles resending the confirmation code to the user. Can attempt to send a maximum of 3 codes to the user to prevent any potential spamming.
   */
  async function resendCode() {
    if (resendCount >= 3 || resendLoading) return;
    setResendLoading(true);
    setResendMessage("");
    setVerifyCode("");  // Clear the input field
    try {
      setResendCount(resendCount + 1);
      const data = await Auth.resendSignUp(userInfo.email);
      if (data && data.success) {
        setResendMessage("Verification code sent.");
        setErrors(prevState => ({ ...prevState, verifyCodeError: "" }));
      } else {
        setErrors(prevState => ({
          ...prevState,
          verifyCodeError: (data && data.error) || "Error sending code.",
        }));
      }
    } catch (error) {
      setErrors(prevState => ({
        ...prevState,
        verifyCodeError: error?.response?.data?.error || "Error sending code.",
      }));
    } finally {
      setResendLoading(false);
    }
  }

  /**
   * Verifies user account using the user provided code.
   */
  async function verifySignUp() {
    setLoading(true);
    try {
      const data = await Auth.confirmSignUp(userInfo.email, verifyCode.trim());
      setLoading(false);
      if (data.success) {
        setCurrentStep(currentStep + 1);
      } else {
        setErrors(prevState => ({
          ...prevState,
          verifyCodeError: data.error || "Invalid code.",
        }));
      }
    } catch (err) {
      setLoading(false);
      setErrors(prevState => ({
        ...prevState,
        verifyCodeError: err.response?.data?.error || "Error verifying code. Please try again.",
      }));
    }
  }

  return (
    <>
      <div className="sm:col-span-2">
        <label htmlFor="verifyCode" className="text-sm sm:text-base text-center block font-medium text-gray-700 dark:text-white">
          Enter the verification code sent to {userInfo.email}
        </label>
        <div className="mt-2">
          <input
            id="verifyCode"
            placeholder="123456"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.trim())}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="6"
            className={classNames(errors.verifyCodeError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'placeholder:text-center text-center border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                verifySignUp();
              }
            }}
          />
        </div>
      </div>

      {errors.verifyCodeError.length > 0 &&
        <div className="mt-1 rounded-md bg-red-100 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HiXCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{errors.verifyCodeError}</h3>
            </div>
          </div>
        </div>
      }

      <div className="mt-6 text-sm sm:text-base gap-14 sm:gap-24 flex justify-between">
        <button
          onClick={() => verifySignUp()}
          type="button"
          className="inline-flex items-center justify-center gap-x-2 rounded-md w-full bg-blue-600 py-3.5 px-3.5 text-md font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Verify Account
          {loading &&
            <ImSpinner8 className="w-5 h-5 text-white animate-spin" />
          }
        </button>
      </div>

      <div className="mt-6 flex justify-center">
        <p className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-400">
          Didn't receive a code?{' '}
          <a
            onClick={() => resendCode()}
            className={classNames(
              resendCount >= 3 || resendLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              'underline font-medium text-blue-700 dark:text-blue-500'
            )}
          >
            {resendLoading ? 'Resending…' : 'Resend Code'}
          </a>
        </p>
      </div>

      {resendMessage && (
        <div className="mt-2 flex justify-center">
          <p className="text-sm sm:text-base font-medium text-green-700 dark:text-green-500">{resendMessage}</p>
        </div>
      )}
    </>
  )
}